import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationEllipsis,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ArrowUpDown, Search, CheckCircle, EyeOff, Loader2, AlertTriangle } from "lucide-react";
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export interface TranslationStatistic {
  id: string;
  gameName: string;
  gameId: number;
  version: string;
  updateDate: string;
  downloads: number;
  status: 'available' | 'hidden';
}
interface TranslationStatsTableProps {
  translatorName?: string;
  translatorId?: number;
}

const TranslationStatsTable: React.FC<TranslationStatsTableProps> = ({ translatorName, translatorId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'hidden'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [statsPerPage, setStatsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{ key: keyof TranslationStatistic; direction: 'ascending' | 'descending' } | null>({ key: 'updateDate', direction: 'descending' });

  const [statistics, setStatistics] = useState<TranslationStatistic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const url = translatorId
          ? `/api/translator_stats/${translatorId}`
          : '/api/translation_stats';
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('A statisztikák betöltése sikertelen.');
        }
        const data = await response.json();
        setStatistics(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ismeretlen hiba történt.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatistics();
  }, [translatorId]);

  const handleSort = (key: keyof TranslationStatistic) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredData = useMemo(() => {
    let data = [...statistics];

    if (statusFilter !== 'all') {
      data = data.filter(item => item.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      data = data.filter(item =>
        item.gameName.toLowerCase().includes(lowerCaseQuery) ||
        item.version.toLowerCase().includes(lowerCaseQuery)
      );
    }

    if (sortConfig !== null) {
      data.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return data;
  }, [statistics, searchQuery, statusFilter, sortConfig]);

  const totalPages = Math.ceil(sortedAndFilteredData.length / statsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * statsPerPage;
    return sortedAndFilteredData.slice(startIndex, startIndex + statsPerPage);
  }, [sortedAndFilteredData, currentPage, statsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleStatsPerPageChange = (value: string) => {
    setStatsPerPage(Number(value));
    setCurrentPage(1);
  };

  const getSortIcon = (key: keyof TranslationStatistic) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="ml-2 opacity-30" />;
    }
    return sortConfig.direction === 'ascending' ? '🔼' : '🔽';
  };

  const getPaginationItems = (currentPage: number, totalPages: number, siblingCount = 1): (number | '...')[] => {
    const totalPageNumbers = siblingCount + 5;

    if (totalPages <= totalPageNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = 3 + 2 * siblingCount;
      let leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = 3 + 2 * siblingCount;
      let rightRange = Array.from({ length: rightItemCount }, (_, i) => totalPages - rightItemCount + 1 + i);
      return [firstPageIndex, '...', ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      let middleRange = Array.from({ length: rightSiblingIndex - leftSiblingIndex + 1 }, (_, i) => leftSiblingIndex + i);
      return [firstPageIndex, '...', ...middleRange, '...', lastPageIndex];
    }

    // This case should ideally not be reached with the logic above, but as a fallback:
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  return (
    <Card className="bg-card text-card-foreground border-border shadow-lg">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-primary">
          {translatorName ? `${translatorName} Statisztikái` : 'Részletes Fordítási Statisztika'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              type="text"
              placeholder="Keresés játék neve vagy verzió alapján..."
              className="w-full pl-10 bg-secondary"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <Select value={statusFilter} onValueChange={(value: 'all' | 'available' | 'hidden') => { setStatusFilter(value); setCurrentPage(1); }}>
            <SelectTrigger className="w-full sm:w-[180px] bg-secondary">
              <SelectValue placeholder="Státusz szűrése" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="all">Összes státusz</SelectItem>
              <SelectItem value="available">Elérhető</SelectItem>
              <SelectItem value="hidden">Rejtett</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statsPerPage.toString()} onValueChange={handleStatsPerPageChange}>
            <SelectTrigger className="w-full sm:w-[150px] bg-secondary">
              <SelectValue placeholder="Sorok száma" />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="10">10 sor</SelectItem>
              <SelectItem value="20">20 sor</SelectItem>
              <SelectItem value="50">50 sor</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64 text-destructive">
            <AlertTriangle className="w-8 h-8 mr-2" /> {error}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => handleSort('updateDate')} className="cursor-pointer hover:bg-secondary">
                    <div className="flex items-center">Feltöltve {getSortIcon('updateDate')}</div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('gameName')} className="cursor-pointer hover:bg-secondary">
                    <div className="flex items-center">Játék neve {getSortIcon('gameName')}</div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('version')} className="cursor-pointer hover:bg-secondary">
                    <div className="flex items-center">Verzió {getSortIcon('version')}</div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('downloads')} className="cursor-pointer hover:bg-secondary">
                    <div className="flex items-center">Letöltések {getSortIcon('downloads')}</div>
                  </TableHead>
                  <TableHead onClick={() => handleSort('status')} className="cursor-pointer hover:bg-secondary">
                    <div className="flex items-center">Státusz {getSortIcon('status')}</div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.updateDate}</TableCell>
                    <TableCell>
                      <Link to={`/games/${item.gameId}`} className="text-primary hover:underline">
                        {item.gameName}
                      </Link>
                    </TableCell>
                    <TableCell>{item.version}</TableCell>
                    <TableCell>{item.downloads.toLocaleString()}</TableCell>
                    <TableCell>
                      <div className={cn("flex items-center gap-2", item.status === 'available' ? 'text-green-400' : 'text-yellow-400')}>
                        {item.status === 'available' ? <CheckCircle size={16} /> : <EyeOff size={16} />}
                        {item.status === 'available' ? 'Elérhető' : 'Rejtett'}
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Nincs megjeleníthető adat.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <Pagination className="mt-8">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious onClick={() => handlePageChange(currentPage - 1)} className={cn(currentPage === 1 && "pointer-events-none opacity-50", "cursor-pointer")} />
              </PaginationItem>
              {getPaginationItems(currentPage, totalPages).map((page, index) =>
                typeof page === 'number' ? (
                  <PaginationItem key={`${page}-${index}`}>
                    <PaginationLink onClick={() => handlePageChange(page)} isActive={currentPage === page} className="cursor-pointer">
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ) : (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                )
              )}
              <PaginationItem>
                <PaginationNext onClick={() => handlePageChange(currentPage + 1)} className={cn(currentPage === totalPages && "pointer-events-none opacity-50", "cursor-pointer")} />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </CardContent>
    </Card>
  );
};

export default TranslationStatsTable;